import flatMap from "array.prototype.flatmap";
import React from "react";
import ReactDOM from "react-dom";
import { Helmet } from "react-helmet-async";
import { RouteComponentProps } from "react-router-dom";

import "../styles/fonts-essay.scss";

import { CardData } from "../CardData";
import {
  isOverlayCoachMarkID,
  isSimpleCoachMarkID,
  OverlayCoachMarkID,
} from "../coachMarks/coachMarkListing";
import { CardHandle } from "../components/Card";
import CoachMarkPopover from "../components/CoachMarkPopover";
import DebugReviewArea from "../components/DebugReviewArea";
import EssayProgressCoachMarkOverlay from "../components/EssayProgressCoachMarkOverlay";
import Hero from "../components/Hero";
import Nav from "../components/Nav";
import Overlay from "../components/Overlay";
import ReviewArea, {
  ReviewAreaMarkingRecord,
  ReviewTask,
} from "../components/ReviewArea";
import {
  CardDataByCardID,
  CardIDsByEssay,
  disconnectIfSSR,
  EssayVersions,
  fetchCardStates,
  fetchUserRecord,
  getExperimentCondition,
  hasSeenCoachMarkID,
  initializeDB,
  logDidContinueReading,
  recordSeenCoachMarkID,
  subscribeToUserChanges,
  UserState,
} from "../db";
import {
  getCardData,
  getCardElements,
  getReviewSetElements,
  getTableOfContents,
  TableOfContents,
} from "../document";
import essayMetadata, { EssayID } from "../essays/essayMetadata";
import essayMetaTags from "../essays/essayMetaTags";
import getEffectiveUserSchedule from "../experiments/getEffectiveUserSchedule";
import FreefloatingLoginUpsell from "../FreefloatingLoginUpsell";
import PatreonButton from "../patreon/PatreonButton";
import { getApproximateNextReviewSessionStatus } from "../reviewSchedule";
import { SpacedRepetitionSchedule } from "../spacedRepetition";
import renderMathInElement from "../util/auto-render";
import getPendingReviewSessionIndex from "../util/getPendingReviewSessionIndex";
import getProgressByEssay, {
  ProgressByEssay,
} from "../util/getProgressByEssay";
import {
  defaultReviewCollectionState,
  getCurrentCardStates,
  getRetryCardIDQueue,
  handleReviewAreaMarking,
  ReviewCollectionState,
} from "../util/reviewCollectionState";
import { debugSSR, renderOrHydrate } from "../util/SSR";
import {
  deserializeOrFetchAllCardData,
  saveAllCardData,
} from "../util/SSRCardData";
import getCurrentPromptIndex from "../util/getCurrentPromptIndex";
import { networkedReviewContext, ReviewContext } from "../util/reviewContext";
import { efficacyExperimentCardIDSet } from "../experiments/efficacy2";
import { isViewingForSSRSnapshot } from "../util/isViewingForSSRSnapshot";

declare global {
  // noinspection JSUnusedGlobalSymbols
  interface Window {
    __CARD_DATA__?: CardData[][] | null;
    __TABLE_OF_CONTENTS__: TableOfContents | null;
    snapSaveState: () => {
      [key: string]: any;
    };
  }
}

interface EssayRouteProps {
  essay: EssayID;
}

interface EssayRouteState extends ReviewCollectionState {
  firstUserState: UserState | null;
  schedule: SpacedRepetitionSchedule;
  // Tracks which review area was used to answer a given card ID so that we can avoid showing retries of that card in that review area.
  previousReviewAreaIndicesByCardID: { [key: string]: number };
  isActivelyReviewing: boolean;

  cardIDsByEssay: CardIDsByEssay | null;
  cardDataByCardID: CardDataByCardID | null;
  essayVersions: EssayVersions | null;
}

let isInitialRender = true;

export default class EssayRoute extends React.Component<
  EssayRouteProps & RouteComponentProps,
  EssayRouteState
> {
  totalCardCount: number | null = null;
  cardData: CardData[][] = [];
  tableOfContents: TableOfContents | null = null;
  essayProgressIndicatorRef: React.RefObject<HTMLDivElement>;

  constructor(props: EssayRouteProps & RouteComponentProps) {
    super(props);
    this.state = {
      ...defaultReviewCollectionState,
      initialCardStates: {},
      userState: "pending",
      userRecord: null,
      firstUserState: null,
      schedule: "aggressiveStart",
      previousReviewAreaIndicesByCardID: {},
      isActivelyReviewing: false,
      coachMarkStateSpec: null,
      cardIDsByEssay: null,
      cardDataByCardID: null,
      essayVersions: null,
    };
    window.snapSaveState = () => {
      console.error("SAVING TOO EARLY");
      return {};
    };

    initializeDB();
    deserializeOrFetchAllCardData().then(allCardData => {
      if (isViewingForSSRSnapshot()) {
        if (!this.cardData || !this.tableOfContents) {
          console.error("Missing card data or ToC");
        }
        window.snapSaveState = () => ({
          __CARD_DATA__: this.cardData,
          __TABLE_OF_CONTENTS__: this.tableOfContents,
        });
        saveAllCardData(allCardData);
        setTimeout(() => {
          disconnectIfSSR().then(() => console.log("Disconnected"));
        }, 5000); // HACK HACK HACK
      }
      this.setState(allCardData);
    });
    if (window.__CARD_DATA__) {
      this.cardData = window.__CARD_DATA__;
      delete window.__CARD_DATA__;
    }
    if (window.__TABLE_OF_CONTENTS__) {
      this.tableOfContents = window.__TABLE_OF_CONTENTS__;
    }

    this.essayProgressIndicatorRef = React.createRef();
    this.onMark = this.onMark.bind(this);
    this.onLogin = this.onLogin.bind(this);
    this.onUserStateChange = this.onUserStateChange.bind(this);
  }

  componentDidMount() {
    this.injectEssayContents().then(() => {
      subscribeToUserChanges(this.onUserStateChange);
      this.processEssayCardData();
      this.processTableOfContents();

      renderMathInElement(document.body);
      this.renderMarginNoteLinks();
      this.renderRoots();

      this.scrollToPathAnchor();
    });
  }

  private onMark(
    markingRecord: ReviewAreaMarkingRecord,
    cardHandle: CardHandle,
    reviewAreaIndex: number,
  ) {
    this.setState(
      {
        previousReviewAreaIndicesByCardID: {
          ...this.state.previousReviewAreaIndicesByCardID,
          [markingRecord.task.cardData.cardID]: reviewAreaIndex,
        },
        ...handleReviewAreaMarking({
          state: this.state,
          markingRecord,
          cardHandle,
          schedule: this.state.schedule,
          retryCoachMarkID: "firstRetryInEssay",
          markingMetadata: {
            essayID: this.props.essay,
            essayVersion: this.state.essayVersions![this.props.essay],
          },
          repeat: () => this.onMark(markingRecord, cardHandle, reviewAreaIndex),
        }),
        isActivelyReviewing: true, // TODO probably clear this if there's no activity in N seconds
      } as EssayRouteState,
      () => {
        // Was that the last card in this review area?
        const reviewAreaCardData = this.cardData[reviewAreaIndex];
        const indexInReviewArea = reviewAreaCardData.findIndex(
          cardData => cardData.cardID === markingRecord.task.cardData.cardID,
        );
        const justFinishedReviewArea =
          indexInReviewArea === reviewAreaCardData.length - 1 ||
          (indexInReviewArea === -1 &&
            getRetryCardIDQueue(this.state).length === 0);

        const hasActiveCoachMark =
          this.state.coachMarkStateSpec &&
          !this.state.coachMarkStateSpec.isDisappearing;
        if (justFinishedReviewArea && !hasActiveCoachMark) {
          // TODO potential but unlikely race condition
          setTimeout(() => this.setState({ isActivelyReviewing: false }), 3000);
        }

        // Consider whether we should show a coach mark.
        if (!hasActiveCoachMark && this.essayProgressIndicatorRef.current) {
          const isNewbie =
            !this.state.userRecord ||
            this.state.userRecord.cardCount === undefined ||
            this.state.userRecord.cardCount < 20;

          let coachMarkIDToShow: OverlayCoachMarkID | null = null;

          // We'll show them a coach mark when they finish their first review session.
          if (
            !hasSeenCoachMarkID(
              "firstReviewAreaComplete",
              this.state.userRecord,
            ) &&
            justFinishedReviewArea &&
            isNewbie
          ) {
            coachMarkIDToShow = "firstReviewAreaComplete";
          }
          // We show a coach mark when they finish the first level.
          else if (
            !hasSeenCoachMarkID("firstLevelComplete", this.state.userRecord)
          ) {
            const progress = this.getProgressByEssay();
            if (
              progress &&
              progress[this.props.essay][0].collectedCardCount ===
                progress[this.props.essay][0].totalCardCount
            ) {
              coachMarkIDToShow = "firstLevelComplete";
            }
          }

          if (coachMarkIDToShow) {
            this.setState({
              coachMarkStateSpec: {
                targetElement: this.essayProgressIndicatorRef.current,
                coachMarkID: coachMarkIDToShow,
                isDisappearing: false,
                onDismiss: () => {
                  return;
                },
              },
              userRecord: recordSeenCoachMarkID(
                coachMarkIDToShow,
                this.state.userRecord,
                this.state.userState,
              ),
            });
          }
        }
      },
    );
  }

  private onLogin() {
    // Intentional no-op.
  }

  private renderAllReviewAreas() {
    if (this.cardData.length === 0) {
      return;
    }

    const isDebug = /debugCards/.test(window.location.href);

    const currentCardStates = getCurrentCardStates(
      this.state.initialCardStates,
      this.state.localCardStates,
    );
    const { cardDataByCardID } = this.state;

    const efficacyExperimentCondition = this.getCurrentEfficacyExperimentCondition();

    const reviewSets = getReviewSetElements(document);
    reviewSets.forEach((reviewSet, index) => {
      const reviewSetCardData = (() => {
        let reviewSetCardData = this.cardData[index];
        if (
          !isDebug &&
          (efficacyExperimentCondition === "delay1Month" ||
            efficacyExperimentCondition === "delay5Days")
        ) {
          // They're enrolled in our efficacy experiment, so we'll remove the experimental cards, unless they've already interacted with them.
          reviewSetCardData = reviewSetCardData.filter(
            ({ cardID }) =>
              currentCardStates[cardID] ||
              !efficacyExperimentCardIDSet.has(cardID),
          );
        }
        return reviewSetCardData;
      })();

      if (isDebug) {
        ReactDOM.render(
          <DebugReviewArea cardDatas={reviewSetCardData} />,
          reviewSet,
        );
        return;
      }

      const incompleteCardDatas = reviewSetCardData.filter(cardData => {
        const cardState = currentCardStates[cardData.cardID];
        return (
          !cardState ||
          (cardState.dueTime.toMillis() < Date.now() && !cardState.needsRetry)
        );
      });

      const orderedCardDatas = cardDataByCardID
        ? incompleteCardDatas.concat(
            flatMap(
              getRetryCardIDQueue(this.state).filter(cardID => {
                const isAfterPreviousReviewAreaIndex =
                  this.state.previousReviewAreaIndicesByCardID[cardID] ===
                    undefined ||
                  index > this.state.previousReviewAreaIndicesByCardID[cardID];
                const isLastOrderedReviewArea = index === reviewSets.length - 1;
                const isLastIncompleteReviewArea = false; // TODO
                return (
                  isAfterPreviousReviewAreaIndex ||
                  isLastOrderedReviewArea ||
                  isLastIncompleteReviewArea
                );
              }),
              cardID => {
                const cardData = cardDataByCardID.get(cardID);
                return cardData ? [cardData] : [];
              },
            ),
          )
        : incompleteCardDatas;

      let tasks: ReviewTask[] = orderedCardDatas.map(
        cardData =>
          ({
            type: "question",
            cardData,
            promptIndex: getCurrentPromptIndex(
              cardData,
              currentCardStates[cardData.cardID],
            ),
            cardState: currentCardStates[cardData.cardID] || null,
          } as ReviewTask),
      ); // shouldn't need this cast, but Typescript can't resolve overloads for unions: https://github.com/microsoft/TypeScript/issues/14107

      let element: React.ReactElement;
      if (reviewSetCardData.length > 0) {
        if (this.state.userState === "anonymous" && index > 1) {
          const targetIndex =
            incompleteCardDatas.length === reviewSetCardData.length ? 1 : 0;
          tasks.splice(targetIndex, 0, {
            type: "login",
            userState: this.state.userState,
          });
        }

        const shouldLabelApplicationPrompts =
          this.state.userState !== "pending" &&
          getExperimentCondition(
            "labelApplicationPrompts",
            this.state.userRecord,
            this.state.userState,
            true,
          ) === "experimental";

        element = (
          <ReviewContext.Provider value={networkedReviewContext}>
            <ReviewArea
              key={`ReviewArea-${index}-${this.state.userState}`}
              tasks={tasks}
              onMark={(markingRecord, cardHandle) =>
                this.onMark(markingRecord, cardHandle, index)
              }
              onLogin={this.onLogin}
              showsCompletedState={
                this.state.userState === "anonymous" ||
                this.state.userRecord !== null
              }
              useSlowTransitions={
                !currentCardStates || Object.keys(currentCardStates).length < 8
              }
              schedule={this.state.schedule}
              showsNeedsRetryNotice
              shouldLabelApplicationPrompts={shouldLabelApplicationPrompts}
            />
          </ReviewContext.Provider>
        );
      } else {
        element = <div />;
      }

      if (!reviewSet.id) {
        reviewSet.id = `reviewSet-${index}`;
      }

      if (!isInitialRender || reviewSet.children[0].className === "card") {
        if (debugSSR) {
          console.log("Re-rendering review area");
        }
        ReactDOM.render(element, reviewSet);
      } else {
        if (debugSSR) {
          console.log("Hydrating review area");
        }
        ReactDOM.hydrate(element, reviewSet);
      }
    });
  }

  private getCurrentEfficacyExperimentCondition() {
    return this.state.userState === "pending"
      ? null
      : getExperimentCondition(
          "efficacy2",
          this.state.userRecord,
          this.state.userState,
          this.state.userState === "anonymous",
        );
  }

  componentDidUpdate(_: EssayRouteProps, prevState: EssayRouteState) {
    this.renderRoots();
  }

  render() {
    const currentCardStates = getCurrentCardStates(
      this.state.initialCardStates,
      this.state.localCardStates,
    );

    const progressByEssay = this.getProgressByEssay();

    const pendingReviewSessionIndex = this.state.userRecord
      ? getPendingReviewSessionIndex(this.state.userRecord)
      : null;

    const approximateNextReviewSessionStatus =
      (pendingReviewSessionIndex !== null &&
        getApproximateNextReviewSessionStatus(
          pendingReviewSessionIndex,
          currentCardStates,
          this.state.schedule,
        )) ||
      null;

    const { coachMarkStateSpec } = this.state;
    let overlay: React.ReactNode;
    if (coachMarkStateSpec) {
      const onDismiss = () => {
        this.setState({
          coachMarkStateSpec: {
            ...this.state.coachMarkStateSpec!,
            isDisappearing: true,
          },
        });
        this.state.coachMarkStateSpec!.onDismiss();
      };

      let overlayInterior:
        | ((interiorRef: React.Ref<HTMLDivElement>) => React.ReactNode)
        | null;
      const { coachMarkID } = coachMarkStateSpec;
      if (isSimpleCoachMarkID(coachMarkID)) {
        overlayInterior = interiorRef => (
          <CoachMarkPopover
            placement="top"
            interiorRef={interiorRef}
            coachMarkID={coachMarkID}
            referenceElement={coachMarkStateSpec.targetElement}
            onCloseAction={onDismiss}
            schedule={this.state.schedule}
          />
        );
      } else if (isOverlayCoachMarkID(coachMarkID)) {
        if (progressByEssay) {
          overlayInterior = interiorRef => (
            <EssayProgressCoachMarkOverlay
              targetElement={coachMarkStateSpec.targetElement}
              hasApplicationPrompts={
                essayMetadata[this.props.essay].hasApplicationPrompts
              }
              interiorRef={interiorRef}
              isPresented={!coachMarkStateSpec.isDisappearing}
              coachMarkID={coachMarkID}
              essayProgress={progressByEssay[this.props.essay]}
              approximateNextReviewSessionStatus={
                approximateNextReviewSessionStatus!
              }
              schedule={this.state.schedule}
            />
          );
        } else {
          overlayInterior = null;
        }
      } else {
        throw new Error(`Unsupported coach mark ${coachMarkID}`);
      }
      overlay = overlayInterior ? (
        <Overlay
          isPresented={!coachMarkStateSpec.isDisappearing}
          onOutsideDismissTap={onDismiss}
          onDidDisappear={() => {
            this.setState({ coachMarkStateSpec: null });
          }}
        >
          {overlayInterior}
        </Overlay>
      ) : null;
    } else {
      overlay = null;
    }

    return (
      <>
        <Helmet>
          <title>{essayMetadata[this.props.essay].title}</title>
          {essayMetaTags[
            this.props.essay
          ]().map((e: React.ReactElement<any>, i: number) =>
            React.cloneElement(e, { ...e.props, key: i }),
          )}
          <script type="application/ld+json">
            {`
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [{
                "@type": "ListItem",
                "position": 1,
                "name": "Quantum Country",
                "item": "https://quantum.country"
              },{
                "@type": "ListItem",
                "position": 2,
                "name": "${essayMetadata[this.props.essay].title}"
              }]
            }`}
          </script>
          <meta name="author" content="Andy Matuschak" />
          <meta name="author" content="Michael Nielsen" />
        </Helmet>
        <div id="EssayRoot">
          <Hero essay={this.props.essay} />
          {this.tableOfContents && (
            <Nav
              progressByEssay={progressByEssay}
              userState={this.state.userState}
              currentLocation={{
                locationType: "essay",
                essayID: this.props.essay,
                tableOfContents: this.tableOfContents,
                approximateNextReviewSessionStatus,
                isActivelyReviewing: this.state.isActivelyReviewing,
              }}
              essayProgressIndicatorRef={this.essayProgressIndicatorRef}
              isShowingEssayProgressIndicatorOverlay={
                (this.state.coachMarkStateSpec &&
                  !this.state.coachMarkStateSpec.isDisappearing &&
                  isOverlayCoachMarkID(
                    this.state.coachMarkStateSpec.coachMarkID,
                  )) ||
                false
              }
              schedule={this.state.schedule}
            />
          )}
        </div>
        {overlay}
      </>
    );
  }

  private getProgressByEssay(): ProgressByEssay | null {
    if (this.state.userState === "pending") {
      return null;
    }

    const currentCardStates = getCurrentCardStates(
      this.state.initialCardStates,
      this.state.localCardStates,
    );
    const efficacyCondition = this.getCurrentEfficacyExperimentCondition();
    return (
      this.state.cardIDsByEssay &&
      getProgressByEssay(
        currentCardStates,
        this.state.cardIDsByEssay,
        this.state.schedule,
        efficacyCondition ? { efficacy2: efficacyCondition } : {},
      )
    );
  }

  private async injectEssayContents() {
    const essayID = this.props.essay;
    let essayContents: string;
    let onLoad: (() => void) | undefined;
    if (essayMetadata[essayID].isRaw) {
      essayContents = (await import(`../essays/${this.props.essay}.html`))
        .default;
      essayContents =
        `<div id="EssayContents">

      <div id="EssayContentsInner">
      
      <div class="BodyColumn">
      ` +
        essayContents +
        "</div></div></div>";
      onLoad = undefined;
    } else {
      const results = await import(`../essays/${this.props.essay}`);
      essayContents = results.default;
      onLoad = results.onLoad;
    }
    if (!document.body.getAttribute("data-contentsLoaded")) {
      document
        .getElementById("react-root")!
        .insertAdjacentHTML("afterend", essayContents);

      // Hack to mark up retina images so that they're displayed at half size.
      for (const imageNode of document.querySelectorAll(
        "img.RetinaImage",
      ) as NodeListOf<HTMLImageElement>) {
        imageNode.srcset = new URL(imageNode.src).pathname.replace(
          /(.+?)\.png$/,
          "$1-1x.png 1x, $1.png 2x",
        );
      }
      document.body.setAttribute("data-contentsLoaded", "true");
    }
    onLoad?.();
  }

  private async onUserStateChange(userState: UserState) {
    const newState: Partial<EssayRouteState> = { userState };
    if (userState !== "pending") {
      if (userState === "registered") {
        this.showRegistrationDependentElements();

        const [cardStates, userRecord] = await Promise.all([
          fetchCardStates(),
          fetchUserRecord(),
        ]);
        newState.initialCardStates = cardStates;
        newState.userRecord = userRecord;

        this.handleContinueReadingRequest(
          new Set(Object.keys(newState.initialCardStates)),
        );
      }

      if (!this.state.firstUserState) {
        newState.firstUserState = userState;
      }

      newState.schedule = getEffectiveUserSchedule(
        getExperimentCondition(
          "schedules202104",
          newState.userRecord || null,
          userState,
          userState === "anonymous",
        ),
      );
    }
    this.setState(newState as EssayRouteState);
  }

  private showRegistrationDependentElements() {
    const transition = (shouldAnimate: boolean) => {
      for (let element of document.getElementsByClassName(
        "newUser",
      ) as HTMLCollectionOf<HTMLElement>) {
        if (shouldAnimate) {
          element.style.opacity = "0";
          element.style.height = "0px";
          setTimeout(() => (element.style.display = "none"), 1000);
        } else {
          element.style.display = "none";
        }
      }
      for (let element of document.getElementsByClassName(
        "curUser",
      ) as HTMLCollectionOf<HTMLElement>) {
        if (shouldAnimate) {
          element.style.opacity = "1";
        }
        element.style.display = "inherit";
      }
    };
    if (this.state.firstUserState === null) {
      transition(false);
    } else {
      setTimeout(() => transition(true), 2000);
    }
  }

  private handleContinueReadingRequest(collectedCardIDs: Set<string>) {
    const currentURL = new URL(document.URL);
    const searchParams = currentURL.searchParams;

    if (searchParams.get("continue") !== null) {
      searchParams.delete("continue");
      window.history.replaceState(
        window.history.state,
        document.title,
        currentURL.toString(),
      );
      const reviewSetIndex =
        this.cardData.length -
        1 -
        [...this.cardData]
          .reverse()
          .findIndex(datas =>
            datas.some(cardData => collectedCardIDs.has(cardData.cardID)),
          );
      console.log("Final review area index:", reviewSetIndex);
      if (reviewSetIndex !== -1) {
        const element = document.getElementById(`reviewSet-${reviewSetIndex}`);
        if (element) {
          const hasAllCards = this.cardData[reviewSetIndex].every(cardData =>
            collectedCardIDs.has(cardData.cardID),
          );
          console.log("Has all cards in review area: ", hasAllCards);
          element.scrollIntoView({
            block: hasAllCards ? "center" : "start",
          });
          logDidContinueReading();
        } else {
          console.error(`Can't find review area ${reviewSetIndex} to continue`);
        }
      }
    }
  }

  private scrollToPathAnchor() {
    // HACK HACK HACK
    setTimeout(() => {
      const hash = this.props.location.hash;
      if (hash) {
        const element = document.querySelector(hash);
        console.log(`Attempting to scroll to ID ${hash}: ${element}`);
        if (element) {
          element.scrollIntoView();
        }
      }
    }, 1000);
  }

  private renderRoots() {
    this.renderAllReviewAreas();

    if (window.location.search.includes("ucla")) {
      for (const exerciseContainer of document.getElementsByClassName(
        "exercises",
      )) {
        (exerciseContainer as HTMLDivElement).style.display = "none";
      }
    }

    for (let loginElement of document.getElementsByClassName("Login")) {
      renderOrHydrate(
        <FreefloatingLoginUpsell
          onLogin={this.onLogin}
          initialUserState={this.state.firstUserState || "pending"}
          userState={this.state.userState}
        />,
        loginElement,
        isInitialRender,
      );
    }

    for (let patreonButton of document.getElementsByClassName(
      "PatreonButton",
    )) {
      renderOrHydrate(<PatreonButton />, patreonButton, isInitialRender);
    }

    isInitialRender = false;
  }

  private renderMarginNoteLinks() {
    if (!isViewingForSSRSnapshot()) {
      for (let marginNote of document.getElementsByClassName(
        "MarginNote",
      ) as HTMLCollectionOf<HTMLElement>) {
        const marker = document.createElement("span");
        marker.className = "MarginNoteMarker";
        marker.onclick = () => {
          let dismiss: (event: Event) => void;
          dismiss = event => {
            if (
              (event.target && marginNote.contains(event.target as Node)) ||
              (typeof TouchEvent !== "undefined" &&
                event instanceof TouchEvent &&
                marginNote.contains(event.touches[0].target as Node))
            ) {
              return;
            }
            marginNote.style.opacity = "0";
            marginNote.style.pointerEvents = "none";
            event.stopPropagation();
            document.removeEventListener("click", dismiss, { capture: true });
            document.removeEventListener("touchstart", dismiss);
          };
          marginNote.style.opacity = "1";
          marginNote.style.pointerEvents = "unset";
          document.addEventListener("touchstart", dismiss);
          document.addEventListener("click", dismiss, {
            capture: true,
          });
        };
        marginNote.parentNode!.insertBefore(marker, marginNote);
      }
    }
  }

  private processEssayCardData() {
    if (this.cardData.length === 0) {
      this.cardData = getReviewSetElements(
        document,
      ).map((reviewSet, reviewSetIndex) =>
        getCardElements(reviewSet).map((cardElement, questionIndex) =>
          getCardData(cardElement, `temp-${reviewSetIndex}-${questionIndex}`),
        ),
      );
    }
  }

  private processTableOfContents() {
    if (this.tableOfContents === null) {
      this.tableOfContents = getTableOfContents();
    }
  }
}
